import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'en' | 'tr';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations: Record<string, Record<Language, string>> = {
  // Navigation
  'nav.home': { en: 'Home', tr: 'Ana Sayfa' },
  'nav.about': { en: 'About', tr: 'Hakkımda' },
  'nav.projects': { en: 'Projects', tr: 'Projeler' },
  'nav.blog': { en: 'Blog', tr: 'Blog' },
  'nav.insights': { en: 'Insights', tr: 'Yazılar' },
  'nav.contact': { en: 'Contact', tr: 'İletişim' },
  
  // Home
  'home.viewProjects': { en: 'View Projects', tr: 'Projeleri Gör' },
  'home.getInTouch': { en: 'Get in Touch', tr: 'İletişime Geç' },
  'home.featuredProjects': { en: 'Featured Projects', tr: 'Öne Çıkan Projeler' },
  'home.viewAll': { en: 'View All', tr: 'Tümünü Gör' },
  'home.awardWinner': { en: 'Award Winner', tr: 'Ödüllü' },
  'home.location': { en: 'Currently based in Istanbul, Türkiye', tr: 'Şu an İstanbul, Türkiye\'de' },
  'home.aboutMe': { en: 'About Me', tr: 'Hakkımda' },
  'home.learnMore': { en: 'Learn More', tr: 'Daha Fazla' },
  'home.latestInsights': { en: 'Learning in Public', tr: 'Son Yazılar' },
  'home.readMore': { en: 'Read More', tr: 'Devamını Oku' },
  'home.ctaTitle': { en: "Let's Create Something Together", tr: 'Birlikte Bir Şeyler Yaratalım' },
  'home.ctaSubtitle': { en: 'Have a project in mind? I\'d love to hear about it. Let\'s discuss how we can bring your vision to life.', tr: 'Aklınızda bir proje mi var? Duymak isterim. Vizyonunuzu nasıl hayata geçirebileceğimizi konuşalım.' },
  
  // About
  'about.title': { en: 'About Me', tr: 'Hakkımda' },
  'about.experience': { en: 'Experience', tr: 'Deneyim' },
  'about.education': { en: 'Education', tr: 'Eğitim' },
  'about.skills': { en: 'Skills', tr: 'Yetenekler' },
  'about.certifications': { en: 'Certifications', tr: 'Sertifikalar' },
  'about.awards': { en: 'Awards', tr: 'Ödüller' },
  'about.publications': { en: 'Publications', tr: 'Yayınlar' },
  'about.languages': { en: 'Languages', tr: 'Diller' },
  'about.present': { en: 'Present', tr: 'Günümüz' },
  
  // Projects
  'projects.title': { en: 'Projects', tr: 'Projeler' },
  'projects.all': { en: 'All', tr: 'Tümü' },
  'projects.viewProject': { en: 'View Project', tr: 'Projeyi Gör' },
  'projects.backToProjects': { en: 'Back to Projects', tr: 'Projelere Dön' },
  'projects.overview': { en: 'Overview', tr: 'Genel Bakış' },
  'projects.gallery': { en: 'Gallery', tr: 'Galeri' },
  
  // Blog
  'blog.title': { en: 'Blog', tr: 'Blog' },
  'blog.subtitle': { en: 'Thoughts, insights and ideas about design and creativity', tr: 'Tasarım ve yaratıcılık hakkında düşünceler, içgörüler ve fikirler' },
  'blog.noPosts': { en: 'No blog posts yet. Check back soon!', tr: 'Henüz blog yazısı yok. Yakında tekrar kontrol edin!' },
  'blog.notFound': { en: 'Post Not Found', tr: 'Yazı Bulunamadı' },
  'blog.backToBlog': { en: 'Back to Blog', tr: 'Blog\'a Dön' },
  'blog.minRead': { en: 'min read', tr: 'dk okuma' },
  
  // Contact
  'contact.title': { en: 'Get in Touch', tr: 'İletişime Geçin' },
  'contact.subtitle': { en: 'Have a project in mind? Let\'s talk.', tr: 'Aklınızda bir proje mi var? Konuşalım.' },
  'contact.name': { en: 'Name', tr: 'İsim' },
  'contact.email': { en: 'Email', tr: 'E-posta' },
  'contact.message': { en: 'Message', tr: 'Mesaj' },
  'contact.send': { en: 'Send Message', tr: 'Mesaj Gönder' },
  'contact.success': { en: 'Message sent successfully!', tr: 'Mesaj başarıyla gönderildi!' },
  'contact.error': { en: 'Failed to send message. Please try again.', tr: 'Mesaj gönderilemedi. Lütfen tekrar deneyin.' },
  'contact.error.network': { en: 'Network error. Please check your connection and try again.', tr: 'Bağlantı hatası. Lütfen internet bağlantınızı kontrol edip tekrar deneyin.' },
  'contact.error.rateLimit': { en: 'Too many messages sent. Please try again later.', tr: 'Çok fazla mesaj gönderildi. Lütfen daha sonra tekrar deneyin.' },
  'contact.error.validation': { en: 'Please check your input and try again.', tr: 'Lütfen girdiğiniz bilgileri kontrol edip tekrar deneyin.' },
  'contact.error.server': { en: 'Server error. Please try again later.', tr: 'Sunucu hatası. Lütfen daha sonra tekrar deneyin.' },
  'contact.findMe': { en: 'Find Me', tr: 'Beni Bulun' },
  
  // Footer
  'footer.navigation': { en: 'Navigation', tr: 'Navigasyon' },
  'footer.social': { en: 'Social', tr: 'Sosyal' },
  'footer.contact': { en: 'Contact', tr: 'İletişim' },
  'footer.rights': { en: 'All rights reserved.', tr: 'Tüm hakları saklıdır.' },
  'footer.madeWith': { en: 'Designed with passion', tr: 'Tutku ile tasarlandı' },
  'footer.privacy': { en: 'Privacy Policy', tr: 'Gizlilik Politikası' },
  'footer.kvkk': { en: 'KVKK Disclosure', tr: 'KVKK Aydınlatma Metni' },
  'footer.cookiePolicy': { en: 'Cookie Policy', tr: 'Çerez Politikası' },

  // Cookie Consent
  'cookie.message': { en: 'We use cookies to analyze site traffic and improve your experience. Analytics cookies are only activated with your consent.', tr: 'Site trafiğini analiz etmek ve deneyiminizi iyileştirmek için çerezler kullanıyoruz. Analitik çerezler yalnızca onayınızla etkinleştirilir.' },
  'cookie.accept': { en: 'Accept', tr: 'Kabul Et' },
  'cookie.reject': { en: 'Reject', tr: 'Reddet' },
  'cookie.learnMore': { en: 'Learn more', tr: 'Daha fazla bilgi' },
  
  // Admin
  'admin.dashboard': { en: 'Dashboard', tr: 'Panel' },
  'admin.projects': { en: 'Projects', tr: 'Projeler' },
  'admin.content': { en: 'Content', tr: 'İçerik' },
  'admin.messages': { en: 'Messages', tr: 'Mesajlar' },
  'admin.logout': { en: 'Logout', tr: 'Çıkış' },
  'admin.login': { en: 'Admin Login', tr: 'Yönetici Girişi' },
  
  // Common
  'common.loading': { en: 'Loading...', tr: 'Yükleniyor...' },
  'common.save': { en: 'Save', tr: 'Kaydet' },
  'common.cancel': { en: 'Cancel', tr: 'İptal' },
  'common.delete': { en: 'Delete', tr: 'Sil' },
  'common.edit': { en: 'Edit', tr: 'Düzenle' },
  'common.add': { en: 'Add', tr: 'Ekle' },
  'common.search': { en: 'Search', tr: 'Ara' },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    return (saved as Language) || 'en';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key: string): string => {
    return translations[key]?.[language] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
